import React, { useState, useEffect } from "react";
import { Box, Typography, Fade, Zoom } from "@mui/material";
import {
    AutoAwesome as CreativeIcon,
    CheckCircle as GritCheckIcon,
    Favorite as KindIcon,
    TrendingUp as GritIcon,
    Lightbulb as CuriousIcon,
} from "@mui/icons-material";
import logo from "../assets/logo.svg";
import { getSplashSpeed } from "../utils/splashSpeed";

const coreValues = [
    { text: "Be Creative", icon: CreativeIcon, delay: 0 },
    { text: "Give a Sh*t", icon: GritCheckIcon, delay: 1000 },
    { text: "Be Kind", icon: KindIcon, delay: 2000 },
    { text: "Have Grit", icon: GritIcon, delay: 3000 },
    { text: "Be Curious", icon: CuriousIcon, delay: 4000 },
];

const SplashScreen = ({ onFinished }) => {
    const [currentPhase, setCurrentPhase] = useState("values");
    const [visibleValues, setVisibleValues] = useState([]);
    const speed = getSplashSpeed();

    useEffect(() => {
        const timers = [];

        coreValues.forEach((value, index) => {
            timers.push(
                setTimeout(() => {
                    setVisibleValues((prev) => [...prev, index]);
                }, value.delay / speed)
            );
        });

        timers.push(setTimeout(() => setCurrentPhase("logo"), 6000 / speed));
        timers.push(setTimeout(() => onFinished(), 8000 / speed));

        return () => timers.forEach(clearTimeout);
    }, [onFinished, speed]);

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100dvh",
                bgcolor: "background.default",
                zIndex: 9999,
                overflow: "hidden",
            }}
        >
            {/* Core Values Phase */}
            <Fade in={currentPhase === "values"} timeout={800 / speed}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: { xs: 2, sm: 3 },
                        px: { xs: 2, sm: 4 },
                    }}
                >
                    <Zoom in={visibleValues.length > 0} timeout={600 / speed}>
                        <Typography
                            sx={{
                                color: "secondary.main",
                                fontWeight: 700,
                                textAlign: "center",
                                fontSize: { xs: "1.2rem", sm: "1.75rem", md: "2.5rem" },
                                lineHeight: 1.2,
                                mb: { xs: 0, sm: 1 },
                            }}
                        >
                            We Stand by Our Core Values
                        </Typography>
                    </Zoom>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: { xs: "12px", sm: 2 },
                            width: "100%",
                            maxWidth: { xs: "100%", sm: "700px", md: "1200px" },
                        }}
                    >
                        {coreValues.map((value, index) => {
                            const IconComponent = value.icon;
                            return (
                                <Zoom
                                    key={index}
                                    in={visibleValues.includes(index)}
                                    timeout={600 / speed}
                                    style={{ transitionDelay: `${100 / speed}ms` }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "background.paper",
                                            borderRadius: 3,
                                            p: { xs: 1.5, sm: 3 },
                                            // Equal width: exactly half the row minus the gap on mobile
                                            width: { xs: "calc(50% - 6px)", sm: "auto" },
                                            minWidth: { xs: 0, sm: 180 },
                                            minHeight: { xs: 110, sm: 150 },
                                            boxShadow: 3,
                                            border: "2px solid",
                                            borderColor: "secondary.main",
                                            boxSizing: "border-box",
                                            animation: visibleValues.includes(index)
                                                ? `pulse ${2 / speed}s ease-in-out infinite`
                                                : "none",
                                            "@keyframes pulse": {
                                                "0%, 100%": {
                                                    boxShadow: "0px 0px 0px 0px rgba(241, 104, 38, 0.4)",
                                                },
                                                "50%": {
                                                    boxShadow: "0px 0px 20px 5px rgba(241, 104, 38, 0.6)",
                                                },
                                            },
                                        }}
                                    >
                                        <IconComponent
                                            sx={{
                                                fontSize: { xs: 32, sm: 48 },
                                                color: "secondary.main",
                                                mb: 1,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                color: "text.primary",
                                                fontWeight: 600,
                                                textAlign: "center",
                                                fontSize: { xs: "0.85rem", sm: "1.1rem", md: "1.25rem" },
                                            }}
                                        >
                                            {value.text}
                                        </Typography>
                                    </Box>
                                </Zoom>
                            );
                        })}
                    </Box>
                </Box>
            </Fade>

            {/* Logo Phase */}
            <Fade in={currentPhase === "logo"} timeout={1000 / speed}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Zoom in={currentPhase === "logo"} timeout={800 / speed}>
                        <Box
                            component="img"
                            src={logo}
                            alt="Creative Electron"
                            sx={{
                                width: { xs: "65%", sm: "45%", md: "30%" },
                                maxHeight: "50vh",
                                height: "auto",
                                display: "block",
                            }}
                        />
                    </Zoom>
                </Box>
            </Fade>
        </Box>
    );
};

export default SplashScreen;
